import React, { CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import http from 'react-syntax-highlighter/dist/esm/languages/prism/http';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ApiService } from '../../services';
import { useNavigate, useParams } from 'react-router';
import MermaidDiagram from './react-mermaid';

SyntaxHighlighter.registerLanguage('http', http);
SyntaxHighlighter.registerLanguage('json', json);

const codeTheme: { [key: string]: React.CSSProperties } = oneLight as {
    [key: string]: React.CSSProperties;
};

type MarkdownDocProps = {
    root: string;        // e.g. "/docs/intro.md"
    folder: string;
    className?: string; // optional wrapper class
    mode?: 'routed' | 'inline';
    linkTarget?: '_blank' | '_self';
    style?: CSSProperties;

};

type MarkdownCodeProps = React.ComponentPropsWithoutRef<'code'> & {
    inline?: boolean;
};

/**
 * Normalizes documentation names by stripping markdown extensions and replacing dashes.
 * @param name Raw file name extracted from markdown links.
 * @returns Clean title-cased label or default fallback.
 */
export const cleanName = (name: string) => {
    return name?.replace(".md", "").replace(/-/g, " ") ?? 'no name found'
}

/**
 * Renders markdown documentation fetched from a specified folder with router-aware navigation.
 * @param root Default markdown file name when no route param is present.
 * @param folder Folder path containing documentation files.
 * @param className Optional wrapper class for styling hooks.
 * @returns Container rendering sanitized markdown content with internal link handling.
 */
export default function MarkdownDoc({ root, folder, className, mode = 'routed', linkTarget = '_blank', style }: MarkdownDocProps) {
    const params = useParams();
    const navigate = useNavigate();
    const [md, setMd] = React.useState<string>('');
    const [_rootUrl, setRootUrl] = React.useState<string>("");
    const [currentFile, setCurrentFile] = React.useState<string>(root);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<Error | null>(null);

    /**
     * Renders fenced markdown code blocks with syntax highlighting and mermaid diagrams.
     */
    const renderCode = ({ inline, className, children, style: _style, ...props }: MarkdownCodeProps) => {
        const match = /language-(\w+)/.exec(className || '');
        const code = String(children).replace(/\n$/, '');
        if (!inline && className === 'language-mermaid') {
            
                return (
                    <MermaidDiagram content={code as string} />
                )
          
        } else if (!inline && match) {
            return (
                <SyntaxHighlighter
                    {...props}
                    style={codeTheme}
                    language={match[1]}
                    PreTag="div"
                >
                    {code}
                </SyntaxHighlighter>
            );
        }


        return (
            <code className={className} {...props}>
                {children}
            </code>
        );
    }

    /**
     * Removes the markdown extension from a path for cleaner routing.
     * @param path Markdown resource path.
     * @returns Path without the trailing `.md` suffix.
     */
    const removeExt = (path: string) => {
        return path.replace(/\.md$/, ''); // remove .md extension
    }

    /**
     * Intercepts link clicks to keep navigation within the documentation router when possible.
     * @param e Anchor click event emitted by markdown links.
     */
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const link = e.currentTarget as HTMLAnchorElement;
        const url = new URL(link.href, _rootUrl || window.location.origin);

        const sameOrigin = url.origin === window.location.origin;
        const inFolder = url.pathname.startsWith(folder);

        if (sameOrigin && inFolder) {
            e.preventDefault();
            let src = decodeURI(url.pathname + url.search + url.hash); // path+qs+hash

            const fileName = src.substring(src.lastIndexOf('/') + 1);

            if (mode === 'routed') {
                navigate(ApiService.cleanUrl(`${folder}/${removeExt(fileName)}`), { replace: false, preventScrollReset: true });
            } else {
                setCurrentFile(fileName);
            }

            loadMarkdown(src);                           // update content
        }
    }

    /**
     * Fetches markdown content for display, tracking loading and error states.
     * @param src Absolute or relative markdown source path.
     * @returns Cleanup function aborting the in-flight fetch.
     */
    const loadMarkdown = (src: string) => {
        const controller = new AbortController();
        const { signal } = controller;

        setLoading(true);
        setError(null);

        if (!src.endsWith('.md')) {
            src = `${src}.md`; // ensure it ends with .md
        }

        fetch(src, { signal })
            .then((r) => {
                if (!r.ok) throw new Error(`Failed to load ${src} (${r.status} ${r.statusText})`);
                return r.text();
            })
            .then((text) => setMd(text))
            .catch((e) => {
                if (signal.aborted) return;
                setError(e instanceof Error ? e : new Error(String(e)));
            })
            .finally(() => {
                if (!signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }

    React.useEffect(() => {


        let file = mode === 'inline' ? currentFile : params.file ?? root;

        let src = `${folder}/${file}`;
        loadMarkdown(src);
        setRootUrl(`${window.location.origin}${folder}`);

        src = ApiService.cleanUrl(src)

        // keep router URL in sync (no .md) so breadcrumbs update
        if (mode === 'routed') {
            const desiredPath = `${folder}/${removeExt(file)}`;
            if (window.location.pathname !== desiredPath) {
                navigate(ApiService.cleanUrl(desiredPath), { replace: true, preventScrollReset: true });
            }
        }

    }, [root, folder, params.file, mode, currentFile]);

    React.useEffect(() => {
        setCurrentFile(root);
    }, [root]);

    if (loading) return <div className={className}>Loading…</div>;
    if (error) return (
        <div className={className} role="alert">
            Error: {error.message}
        </div>
    );

    return (
        <div className={`${className ?? ""} fbx-documentation`} style={style}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} children={md} rehypePlugins={[rehypeSanitize]} components={{
                img: ({ node, ...props }) => {

                    let src = props.src ?? '';
                    let adj = new URL(src, _rootUrl).href; // ensure absolute URL
                    return (<img src={adj} />)
                },
                a: ({ node, ...props }) => {

                    const url = new URL(props.href ?? '', _rootUrl);
                    const label = cleanName(url.href); // optional, just for display
                    const href = removeExt(url.href); // remove .md extension
                    return (
                        <a href={href} target={linkTarget} rel={linkTarget === '_blank' ? 'noreferrer' : undefined} onClick={handleLinkClick}>
                            {props.children || label}
                        </a>
                    );
                },
                code: renderCode
            }} />

        </div>
    );
}
